
/**
 * Module dependencies.
 */

import { Form, Heading, PasswordInput, SubmitButton, Text, useSnackbar } from 'slyk-web-libs/design-system';
import { Helmet } from 'react-helmet';
import { MainContent } from 'client/components/layouts/page-layout';
import { Payspace } from 'slyk-web-libs/types';
import { Redirect, useHistory } from 'react-router-dom';
import { color, media } from 'react-components/styles';
import { getNetworkErrorTranslationPaths } from 'slyk-web-libs/core';
import { gql, useMutation } from '@apollo/client';
import { password } from 'slyk-web-libs/regex';
import { usePayspace } from 'slyk-web-libs/payspace-context';
import { useQueryString } from 'client/hooks/use-query-string';
import { useTranslate } from 'slyk-web-libs/i18n';
import React, { useCallback } from 'react';
import routes from 'slyk-web-libs/config/payspace-routes';
import slykEndpoints from 'slyk-web-libs/config/slyk-endpoints';
import styled from 'styled-components';

/**
 * Reset password mutation.
 */

const resetPasswordMutation = gql`
  mutation ResetPassword(
    $password: String!
    $payspaceSlug: String!
    $token: String!
  ) {
    resetPassword(
      input: {
        domain: $payspaceSlug
        password: $password
        token: $token
      }
    ) @rest(
      method: "POST"
      path: "${slykEndpoints.resetPassword}"
    ) {
      NoResponse
    }
  }
`;

/**
 * Json schema.
 */

const jsonSchema = {
  additionalProperties: false,
  properties: {
    password: {
      pattern: password,
      type: 'string'
    }
  },
  required: ['password'],
  type: 'object'
};

/**
 * `Content` styled component.
 */

const Content = styled.div` 
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Heading).attrs({ as: 'h1' })` 
  color: ${color('primary')};
  font-weight: 600;
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text)` 
  color: ${color('gray500')};
  margin-bottom: 24px;

  ${media.min('md')`
    margin-bottom: 40px;
  `}
`;

/**
 * Export `ResetPassword` container.
 */

export function ResetPassword(): JSX.Element {
  const { translate } = useTranslate();
  const { showErrorMessage } = useSnackbar();
  const { token } = useQueryString();
  const history = useHistory();
  const { slug } = usePayspace() as Payspace;
  const title = translate('resetPassword.title');
  const [resetPassword] = useMutation(resetPasswordMutation, {
    onCompleted: () => {
      history.push(routes.resetPasswordSuccess);
    },
    onError: (error: any) => {
      showErrorMessage(translate(getNetworkErrorTranslationPaths(
        error?.networkError?.result?.code,
        'resetPassword.errors'
      )));
    }
  });

  const handleSubmit = useCallback(({ password }: { password: string }) => {
    return resetPassword({
      variables: {
        password,
        payspaceSlug: slug,
        token
      }
    });
  }, [slug, resetPassword, token]);

  if (!token) {
    return <Redirect to={routes.home} />;
  }

  return (
    <>
      <Helmet>
        <title>
          {title}
        </title>
      </Helmet>

      <MainContent>
        <Content>
          <Title>
            {title}
          </Title>

          <Description>
            {translate('resetPassword.description')}
          </Description>

          <Form
            jsonSchema={jsonSchema}
            onSubmit={handleSubmit}
          >
            <PasswordInput
              autoFocus
              hasRequirements
              label={translate('resetPassword.form.password')}
              name={'password'}
            />

            <SubmitButton
              fullWidth
              size={'large'}
            >
              {translate('resetPassword.form.submit')}
            </SubmitButton>
          </Form>
        </Content>
      </MainContent>
    </>
  );
}
